import React, { useCallback, useEffect, useState } from "react";
import { adminApi } from "./adminApi";

const NAVY = "#1E3A5F";
const inr = (paise) => `₹${(Number(paise || 0) / 100).toLocaleString("en-IN")}`;
const fmtDate = (s) => { try { return s ? new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—"; } catch { return "—"; } };

const card = { border: "1px solid #e2e8f0", borderRadius: 14, background: "#fff", padding: 20, marginTop: 24 };
const CODE_LABELS = { trial: "Trial", monthly: "Pulse (Monthly)", yearly: "Command (Yearly)", lifetime: "Sovereign (1 Year)" };

const emptyForm = (code = "monthly") => ({
  code, name: "", short_description: "", amount_rupees: "", trial_days: 0,
  razorpay_plan_id: "", badge: "", button_text: "", display_order: 100,
  is_active: true, is_visible: true, highlight: false, features: "",
  effective_from: "", effective_until: "",
});

export default function SubscriptionManagement() {
  const [loading, setLoading] = useState(true);
  const [migration, setMigration] = useState(null);
  const [data, setData] = useState({ versions: [], active_by_code: {}, canonical_codes: [], razorpay_env_plan_ids: {} });
  const [editing, setEditing] = useState(null); // { mode:'create'|'edit', id?, form }
  const [saveErr, setSaveErr] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setMigration(null);
    const res = await adminApi.listPlanConfigs();
    if (res.ok) setData(res.data);
    else if (res.status === 503) setMigration(typeof res.error === "string" ? res.error : (res.data?.detail || "Migration required."));
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const notify = (m) => { setToast(m); setTimeout(() => setToast(null), 3500); };

  const openCreate = (code) => { setSaveErr(null); setEditing({ mode: "create", form: emptyForm(code) }); };
  const openEdit = (cfg) => {
    setSaveErr(null);
    setEditing({ mode: "edit", id: cfg.id, form: {
      code: cfg.code, name: cfg.name || "", short_description: cfg.short_description || "",
      amount_rupees: (Number(cfg.amount_paise || 0) / 100).toString(), trial_days: cfg.trial_days || 0,
      razorpay_plan_id: cfg.razorpay_plan_id || "", badge: cfg.badge || "", button_text: cfg.button_text || "",
      display_order: cfg.display_order || 100, is_active: !!cfg.is_active, is_visible: !!cfg.is_visible,
      highlight: !!cfg.highlight, features: (cfg.features || []).join("\n"),
      effective_from: (cfg.effective_from || "").slice(0, 10), effective_until: (cfg.effective_until || "").slice(0, 10),
    } });
  };

  const setF = (k, v) => setEditing((e) => ({ ...e, form: { ...e.form, [k]: v } }));
  const isRecurring = editing && ["monthly", "yearly"].includes(editing.form.code);

  const save = async () => {
    setSaving(true); setSaveErr(null);
    const f = editing.form;
    const payload = {
      name: f.name, short_description: f.short_description || null,
      amount_paise: Math.round(parseFloat(f.amount_rupees || "0") * 100),
      trial_days: parseInt(f.trial_days || 0, 10),
      razorpay_plan_id: f.razorpay_plan_id.trim() || null,
      badge: f.badge || null, button_text: f.button_text || null,
      display_order: parseInt(f.display_order || 100, 10),
      is_active: f.is_active, is_visible: f.is_visible, highlight: f.highlight,
      features: f.features.split("\n").map((s) => s.trim()).filter(Boolean),
      effective_from: f.effective_from || null, effective_until: f.effective_until || null,
    };
    let res;
    if (editing.mode === "create") res = await adminApi.createPlanConfig({ code: f.code, ...payload });
    else res = await adminApi.updatePlanConfig(editing.id, { ...payload, confirm: true });
    setSaving(false);
    if (res.ok) { setEditing(null); notify(editing.mode === "create" ? "New plan version created." : "Plan updated."); load(); }
    else {
      const d = res.data?.detail;
      setSaveErr(typeof d === "object" ? (d.hint || d.error) : (d || res.error || "Could not save."));
    }
  };

  if (loading) return <div style={card} data-testid="subscription-management-loading">Loading plans…</div>;

  return (
    <div style={card} data-testid="subscription-management">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <div>
          <h3 style={{ margin: 0, color: NAVY, fontSize: 16, fontWeight: 800 }}>Subscription Management</h3>
          <p style={{ margin: "2px 0 0", fontSize: 12.5, color: "#64748b" }}>Manage pricing, features & Razorpay mapping — no code changes needed.</p>
        </div>
        <button onClick={load} data-testid="sm-refresh" style={{ fontSize: 12.5, padding: "6px 12px", border: "1px solid #e2e8f0", borderRadius: 8, background: "#fff", cursor: "pointer" }}>Refresh</button>
      </div>

      {migration ? (
        <div data-testid="sm-migration-required" style={{ marginTop: 14, padding: 14, border: "1px solid #fcd34d", background: "#fffbeb", borderRadius: 10, fontSize: 12.5, color: "#92400e" }}>
          <b>One-time setup required.</b> Run <code>backend/migrations/2026-08_plan_configs.sql</code> in Supabase (preview → prod), then Refresh. Until then, live pricing uses the code catalog.
          <div style={{ marginTop: 6, opacity: 0.8 }}>{migration}</div>
        </div>
      ) : (
        <>
          {/* Active plan cards */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(220px,1fr))", gap: 12, marginTop: 16 }}>
            {(data.canonical_codes || []).map((code) => {
              const c = data.active_by_code?.[code];
              return (
                <div key={code} data-testid={`sm-plan-${code}`} style={{ border: "1px solid #e2e8f0", borderRadius: 12, padding: 14 }}>
                  <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, color: "#94a3b8", fontWeight: 700 }}>{CODE_LABELS[code] || code}</div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: NAVY, marginTop: 4 }}>{c ? (code === "trial" ? "Free" : inr(c.amount_paise)) : "—"}</div>
                  <div style={{ fontSize: 11.5, color: "#64748b" }}>{c?.name || "no active version"}</div>
                  {["monthly", "yearly"].includes(code) && (
                    <div style={{ fontSize: 11, marginTop: 6, color: c?.razorpay_plan_id ? "#16a34a" : "#dc2626" }} data-testid={`sm-rzp-${code}`}>
                      {c?.razorpay_plan_id ? `↳ ${c.razorpay_plan_id}` : "no Razorpay mapping"}
                    </div>
                  )}
                  <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
                    {c && <button onClick={() => openEdit(c)} data-testid={`sm-edit-${code}`} style={btnSm(NAVY)}>Edit</button>}
                    <button onClick={() => openCreate(code)} data-testid={`sm-newversion-${code}`} style={btnSmOutline}>New version</button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* All versions table */}
          <div style={{ overflowX: "auto", marginTop: 18 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }} data-testid="sm-versions-table">
              <thead>
                <tr style={{ textAlign: "left", color: "#64748b", borderBottom: "1px solid #e2e8f0" }}>
                  {["Plan", "Price", "Interval", "Trial", "Razorpay", "Visible", "Active", "Effective from", "Until", ""].map((h) => (
                    <th key={h} style={{ padding: "8px 10px", fontWeight: 600 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(data.versions || []).map((v) => (
                  <tr key={v.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                    <td style={td}>{CODE_LABELS[v.code] || v.code}<div style={{ color: "#94a3b8", fontSize: 11 }}>{v.name}</div></td>
                    <td style={td}>{v.code === "trial" ? "Free" : inr(v.amount_paise)}</td>
                    <td style={td}>{v.interval || (v.billing_type === "lifetime" ? "one-time" : "—")}</td>
                    <td style={td}>{v.trial_days || 0}d</td>
                    <td style={{ ...td, fontFamily: "monospace", fontSize: 11 }}>{v.razorpay_plan_id || "—"}</td>
                    <td style={td}>{v.is_visible ? "Yes" : "No"}</td>
                    <td style={td}><span style={{ color: v.is_active ? "#16a34a" : "#94a3b8", fontWeight: 600 }}>{v.is_active ? "Active" : "Inactive"}</span></td>
                    <td style={td}>{fmtDate(v.effective_from)}</td>
                    <td style={td}>{fmtDate(v.effective_until)}</td>
                    <td style={td}><button onClick={() => openEdit(v)} data-testid={`sm-edit-version-${v.id}`} style={btnSmOutline}>Edit</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Editor modal */}
      {editing && (
        <div style={overlay} data-testid="sm-editor" onClick={() => !saving && setEditing(null)}>
          <div style={modal} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: 0, color: NAVY, fontSize: 15, fontWeight: 800 }}>
              {editing.mode === "create" ? "New plan version" : "Edit plan"} — {CODE_LABELS[editing.form.code] || editing.form.code}
            </h3>
            {isRecurring && editing.mode === "edit" && (
              <div style={{ marginTop: 8, padding: 10, background: "#fffbeb", border: "1px solid #fcd34d", borderRadius: 8, fontSize: 11.5, color: "#92400e" }} data-testid="sm-rzp-warning">
                Changing price requires creating a new Razorpay Plan. Existing subscriptions continue using their original Razorpay plan — enter the NEW Razorpay Plan ID below.
              </div>
            )}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 12 }}>
              {editing.mode === "create" && (
                <Field label="Plan code">
                  <select value={editing.form.code} onChange={(e) => setF("code", e.target.value)} data-testid="sm-f-code" style={input}>
                    {(data.canonical_codes || []).map((c) => <option key={c} value={c}>{CODE_LABELS[c] || c}</option>)}
                  </select>
                </Field>
              )}
              <Field label="Name"><input value={editing.form.name} onChange={(e) => setF("name", e.target.value)} data-testid="sm-f-name" style={input} /></Field>
              <Field label="Price (₹)"><input type="number" value={editing.form.amount_rupees} onChange={(e) => setF("amount_rupees", e.target.value)} data-testid="sm-f-amount" style={input} /></Field>
              <Field label="Trial days"><input type="number" value={editing.form.trial_days} onChange={(e) => setF("trial_days", e.target.value)} data-testid="sm-f-trialdays" style={input} /></Field>
              <Field label="Razorpay Plan ID (recurring)"><input value={editing.form.razorpay_plan_id} onChange={(e) => setF("razorpay_plan_id", e.target.value)} data-testid="sm-f-rzp" style={input} placeholder="plan_..." /></Field>
              <Field label="Badge"><input value={editing.form.badge} onChange={(e) => setF("badge", e.target.value)} data-testid="sm-f-badge" style={input} placeholder="Most Popular" /></Field>
              <Field label="Button text"><input value={editing.form.button_text} onChange={(e) => setF("button_text", e.target.value)} style={input} /></Field>
              <Field label="Display order"><input type="number" value={editing.form.display_order} onChange={(e) => setF("display_order", e.target.value)} style={input} /></Field>
              <Field label="Effective from"><input type="date" value={editing.form.effective_from} onChange={(e) => setF("effective_from", e.target.value)} data-testid="sm-f-from" style={input} /></Field>
              <Field label="Effective until"><input type="date" value={editing.form.effective_until} onChange={(e) => setF("effective_until", e.target.value)} style={input} /></Field>
            </div>
            <Field label="Short description"><input value={editing.form.short_description} onChange={(e) => setF("short_description", e.target.value)} style={input} /></Field>
            <Field label="Features (one per line)"><textarea value={editing.form.features} onChange={(e) => setF("features", e.target.value)} data-testid="sm-f-features" style={{ ...input, height: 84, resize: "vertical" }} /></Field>
            <div style={{ display: "flex", gap: 16, marginTop: 8, fontSize: 12.5 }}>
              <label style={chk}><input type="checkbox" checked={editing.form.is_active} onChange={(e) => setF("is_active", e.target.checked)} data-testid="sm-f-active" /> Active</label>
              <label style={chk}><input type="checkbox" checked={editing.form.is_visible} onChange={(e) => setF("is_visible", e.target.checked)} data-testid="sm-f-visible" /> Visible</label>
              <label style={chk}><input type="checkbox" checked={editing.form.highlight} onChange={(e) => setF("highlight", e.target.checked)} /> Highlight</label>
            </div>
            {saveErr && <div data-testid="sm-save-error" style={{ marginTop: 10, color: "#dc2626", fontSize: 12.5 }}>{saveErr}</div>}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
              <button onClick={() => setEditing(null)} disabled={saving} style={btnSmOutline}>Cancel</button>
              <button onClick={save} disabled={saving} data-testid="sm-save" style={btnSm(NAVY)}>{saving ? "Saving…" : "Save"}</button>
            </div>
          </div>
        </div>
      )}
      {toast && <div style={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", background: "#0f172a", color: "#fff", padding: "10px 16px", borderRadius: 8, fontSize: 13, zIndex: 60 }} data-testid="sm-toast">{toast}</div>}
    </div>
  );
}

const td = { padding: "8px 10px", color: "#334155" };
const input = { width: "100%", boxSizing: "border-box", border: "1px solid #e2e8f0", borderRadius: 8, padding: "8px 10px", fontSize: 13 };
const chk = { display: "flex", alignItems: "center", gap: 6, color: "#334155" };
const overlay = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, zIndex: 55 };
const modal = { background: "#fff", borderRadius: 16, padding: 22, width: "100%", maxWidth: 640, maxHeight: "90vh", overflowY: "auto" };
const btnSm = (bg) => ({ fontSize: 12.5, fontWeight: 600, padding: "6px 12px", border: "none", borderRadius: 8, background: bg, color: "#fff", cursor: "pointer" });
const btnSmOutline = { fontSize: 12.5, fontWeight: 500, padding: "6px 12px", border: "1px solid #e2e8f0", borderRadius: 8, background: "#fff", color: "#334155", cursor: "pointer" };

const Field = ({ label, children }) => (
  <label style={{ display: "block", fontSize: 11.5, color: "#64748b", marginTop: 10 }}>
    <span style={{ display: "block", marginBottom: 4 }}>{label}</span>{children}
  </label>
);
